import { EggCondition, EggRoutingRule } from "../../types";

// Utility di normalizzazione: i valori nel payload EGG sono spesso stringhe
// ("657") anche quando semanticamente numerici, quindi confrontiamo per stringa.
const s = (v: unknown): string => String(v);
const asSet = (v: unknown): string[] => (Array.isArray(v) ? v.map(s) : [s(v)]);

/**
 * True quando due condizioni SULLO STESSO campo non possono mai valere insieme
 * (contraddizione dimostrabile). Conservativo: ritorna true solo quando è certo;
 * nei casi ambigui ritorna false (→ trattate come potenzialmente sovrapponibili).
 */
export function conditionsContradict(a: EggCondition, b: EggCondition): boolean {
  const eqA = a.operator === "==";
  const eqB = b.operator === "==";

  // == vs ==  → contraddicono se i valori differiscono
  if (eqA && eqB) return s(a.value) !== s(b.value);

  // == vs !=  → contraddicono se stesso valore
  if (eqA && b.operator === "!=") return s(a.value) === s(b.value);
  if (eqB && a.operator === "!=") return s(a.value) === s(b.value);

  // == vs in  → contraddicono se il valore non è nell'insieme
  if (eqA && b.operator === "in") return !asSet(b.value).includes(s(a.value));
  if (eqB && a.operator === "in") return !asSet(a.value).includes(s(b.value));

  // == vs not_in → contraddicono se il valore è nell'insieme escluso
  if (eqA && b.operator === "not_in") return asSet(b.value).includes(s(a.value));
  if (eqB && a.operator === "not_in") return asSet(a.value).includes(s(b.value));

  // in vs in → contraddicono se gli insiemi sono disgiunti
  if (a.operator === "in" && b.operator === "in") {
    const setB = new Set(asSet(b.value));
    return !asSet(a.value).some((x) => setB.has(x));
  }

  // in vs not_in → contraddicono se ogni valore ammesso è escluso
  if (a.operator === "in" && b.operator === "not_in") {
    const excluded = new Set(asSet(b.value));
    return asSet(a.value).every((x) => excluded.has(x));
  }
  if (b.operator === "in" && a.operator === "not_in") {
    const excluded = new Set(asSet(a.value));
    return asSet(b.value).every((x) => excluded.has(x));
  }

  // Range numerici
  const na = Number(a.value);
  const nb = Number(b.value);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) {
    const gt = (o: string) => o === ">" || o === ">=";
    const lt = (o: string) => o === "<" || o === "<=";

    // a: lower bound (>/>=), b: upper bound (</<=) → contraddicono se non si sovrappongono
    if (gt(a.operator) && lt(b.operator)) {
      return a.operator === ">=" && b.operator === "<=" ? na > nb : na >= nb;
    }
    if (lt(a.operator) && gt(b.operator)) {
      return a.operator === "<=" && b.operator === ">=" ? nb > na : nb >= na;
    }
    // == vs range
    if (eqA && gt(b.operator)) return b.operator === ">" ? !(na > nb) : !(na >= nb);
    if (eqA && lt(b.operator)) return b.operator === "<" ? !(na < nb) : !(na <= nb);
    if (eqB && gt(a.operator)) return a.operator === ">" ? !(nb > na) : !(nb >= na);
    if (eqB && lt(a.operator)) return a.operator === "<" ? !(nb < na) : !(nb <= na);
  }

  return false;
}

/**
 * True quando due regole non possono mai matchare lo stesso evento.
 * Dimostrabile solo con match_mode "all" (AND): basta una coppia di condizioni
 * contraddittorie sullo stesso campo per rendere insoddisfacibile A∧B.
 * Con "any" (OR) o senza condizioni non tentiamo la prova (→ non esclusive).
 */
export function rulesMutuallyExclusive(a: EggRoutingRule, b: EggRoutingRule): boolean {
  if (a.match_mode !== "all" || b.match_mode !== "all") return false;
  const ca = a.conditions ?? [];
  const cb = b.conditions ?? [];
  if (ca.length === 0 || cb.length === 0) return false;

  for (const x of ca) {
    for (const y of cb) {
      if (x.field === y.field && conditionsContradict(x, y)) return true;
    }
  }
  return false;
}

export interface OverlapInfo {
  /** event_type → regole attive che possono realmente co-scattare su di esso. */
  shared: { event: string; rules: EggRoutingRule[] }[];
  /** id delle regole coinvolte in una sovrapposizione reale. */
  overlappingRuleIds: Set<string>;
  /** id delle regole attive senza condizioni di trigger (scattano sempre). */
  alwaysFireRuleIds: Set<string>;
}

/**
 * Calcola le sovrapposizioni reali tra regole attive e le regole "sempre attive".
 * Una coppia di regole sullo stesso event_type è una sovrapposizione reale a meno
 * che non sia dimostrabilmente mutuamente esclusiva.
 */
export function computeOverlaps(rules: EggRoutingRule[]): OverlapInfo {
  const active = rules.filter((r) => r.active);

  const alwaysFireRuleIds = new Set<string>(
    active.filter((r) => (r.conditions?.length ?? 0) === 0).map((r) => r.id),
  );

  const byEvent = new Map<string, EggRoutingRule[]>();
  for (const r of active) {
    for (const ev of r.event_types) {
      const arr = byEvent.get(ev) ?? [];
      arr.push(r);
      byEvent.set(ev, arr);
    }
  }

  const shared: { event: string; rules: EggRoutingRule[] }[] = [];
  const overlappingRuleIds = new Set<string>();

  byEvent.forEach((rs, ev) => {
    if (rs.length < 2) return;
    const involved = new Map<string, EggRoutingRule>();
    for (let i = 0; i < rs.length; i++) {
      for (let j = i + 1; j < rs.length; j++) {
        if (!rulesMutuallyExclusive(rs[i], rs[j])) {
          involved.set(rs[i].id, rs[i]);
          involved.set(rs[j].id, rs[j]);
        }
      }
    }
    if (involved.size > 0) {
      shared.push({ event: ev, rules: Array.from(involved.values()) });
      involved.forEach((_, id) => overlappingRuleIds.add(id));
    }
  });

  return { shared, overlappingRuleIds, alwaysFireRuleIds };
}
